import { useCallback, useEffect, useMemo, useState } from 'react'
import useReactWebSocket, { ReadyState } from 'react-use-websocket'

import {
  WebsocketAuthenticatedStatus,
  WebsocketAuthenticationErrorResponse,
  WebsocketAuthenticationResponse,
  WebsocketAuthenticationStatus,
  WebsocketAuthenticationSuccessResponse,
  WebsocketErrorResponse,
  WebsocketMessage,
} from './types'
import { DEFAULT_WEBSOCKET_CONFIG } from './config'
import { authUtils } from '@/utils/auth'
import { Logger } from '@/lib/logger'

const logger = new Logger('WebSocket')

interface UseWebsocketParams {
  url?: string
  enabled?: boolean
  options?: {
    auth: {
      payload?: Record<string, unknown>
      protected?: boolean
      autoConnect?: boolean
      onAuthenticated?: () => void
      onAuthenticationError?: (error: string) => void
    }
    onError?: (status: string, message: string) => void
  }
}

const isSuccessAuthenticatedMessage = (
  message: WebsocketAuthenticationSuccessResponse
): message is WebsocketAuthenticationSuccessResponse => {
  return 'status' in message && WebsocketAuthenticatedStatus.includes(message.status)
}

const isErrorAuthenticatedMessage = (
  message: WebsocketAuthenticationErrorResponse
): message is WebsocketAuthenticationErrorResponse => {
  return 'error' in message
}

const isErrorResponse = (message: WebsocketMessage<WebsocketErrorResponse>): message is WebsocketErrorResponse => {
  return 'status' in message && message.status === 'error'
}

const isSystemMessage = (message: WebsocketMessage): message is WebsocketAuthenticationResponse => {
  return (
    isSuccessAuthenticatedMessage(message as WebsocketAuthenticationSuccessResponse) ||
    isErrorAuthenticatedMessage(message as WebsocketAuthenticationErrorResponse)
  )
}

export function useWebsocket<TMessage = unknown>({
  url = process.env.REACT_APP_WS_URL || '',
  enabled = true,
  options = {
    auth: {
      payload: {},
      protected: true,
      autoConnect: false,
    },
  },
}: UseWebsocketParams) {
  const [authenticated, setAuthenticated] = useState(false)
  const { auth } = options

  const handleMessages = useCallback((event: MessageEvent) => {
    const message = JSON.parse(event.data)

    if (isSuccessAuthenticatedMessage(message) && message.status === WebsocketAuthenticationStatus.AUTHENTICATED) {
      setAuthenticated(true)
      auth.onAuthenticated?.()
    } else if (isErrorAuthenticatedMessage(message)) {
      logger.error('WebSocket authentication error:', message.error)
      setAuthenticated(false)
      auth.onAuthenticationError?.(message.error)
    } else if (isErrorResponse(message)) {
      logger.error('WebSocket error:', message.message)
      options.onError?.(message.status, message.message)
    }
  }, [])

  const handleCloseConnection = useCallback(() => {
    setAuthenticated(false)
  }, [])

  const socketConfig = useMemo(
    () => ({
      ...DEFAULT_WEBSOCKET_CONFIG,
      onMessage: handleMessages,
      onClose: handleCloseConnection,
    }),
    [handleMessages, handleCloseConnection]
  )

  const {
    readyState: connectionState,
    sendJsonMessage,
    lastJsonMessage,
  } = useReactWebSocket(enabled && url ? url : null, socketConfig)

  const connected = connectionState === ReadyState.OPEN && authenticated

  const send = useCallback(
    (message: unknown) => {
      if (!connected) return null
      sendJsonMessage(message)
    },
    [connected, sendJsonMessage]
  )

  const authenticate = useCallback(async () => {
    if (connectionState !== ReadyState.OPEN || authenticated) return

    if (auth.protected) {
      const { accessToken } = await authUtils.getStoredTokens()

      if (!accessToken) return
      sendJsonMessage({ token: accessToken, ...auth.payload })
    } else {
      sendJsonMessage({ ...auth.payload })
    }
  }, [connectionState, authenticated])

  const receive = useCallback(() => {
    if (!connected || !lastJsonMessage) return null
    if (isSystemMessage(lastJsonMessage)) return null

    return lastJsonMessage as WebsocketMessage<TMessage>
  }, [connected, lastJsonMessage])

  useEffect(() => {
    if (!auth.autoConnect) return
    authenticate()
  }, [authenticated, connectionState])

  return {
    receive,
    send,
    authenticate,
    authenticated,
    connected,
    connectionState,
  }
}
